import type { ROUTES } from '@ui/constants/routes';
import { isRoute } from '@ui/utils/isRoute';
import { isUrlObject } from '@ui/utils/isUrlObject';
import type { NextRouter } from 'next/router';
import { useRouter as useNextRouter } from 'next/router';
import { useMemo } from 'react';
import type { UrlObject } from 'url';

import { localeToPath } from './localeToPath';
import { useLocale } from './useLocale';

const getPathWithLocale = ({
  locale,
  pathname,
}: {
  locale: string;
  pathname: ROUTES;
}) => {
  return `${localeToPath(locale)}${pathname}`;
};

const useRouterWithLocale = (): NextRouter => {
  const router = useNextRouter();
  const locale = useLocale();

  return useMemo(() => {
    return {
      ...router,
      prefetch(url, asPath, options) {
        const newUrl = getNewUrl(url, locale);
        if (isUrlObject(newUrl)) {
          return router.prefetch(newUrl.pathname, asPath, options);
        }

        return router.prefetch(newUrl, asPath, options);
      },
      replace(url, ...rest) {
        return router.replace(getNewUrl(url, locale), ...rest);
      },
      push(url, ...rest) {
        return router.push(getNewUrl(url, locale), ...rest);
      },
    };
  }, [locale, router]);
};

const getNewUrl = (
  url: UrlObject | string,
  locale: string,
):
  | (UrlObject & {
      pathname: string;
    })
  | string => {
  const pathname = isUrlObject(url) ? url.pathname || '' : url || '';

  if (!isRoute(pathname)) {
    throw new Error(
      'Internal Route Invalid, should use value from ROUTES, if passing a UrlObject, put the value in UrlObject.pathname',
    );
  }
  const newPathname = getPathWithLocale({
    locale,
    pathname: pathname,
  });

  return isUrlObject(url)
    ? {
        ...url,
        pathname: newPathname,
      }
    : newPathname;
};

// alias for smooth refactor
export const useRouter = useRouterWithLocale;
export type { NextRouter, RouterEvent, SingletonRouter } from 'next/router';
export { Router, withRouter } from 'next/router';
